import CompanyTypePage from './CompanyTypePage';
import { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';

const CONFIG = {
  icon: '🤝',
  name: 'Limited Liability Partnership (LLP)',
  shortName: 'LLP',
  subtitle: 'Flexible hybrid entity combining partnership benefits with limited liability · LLP Act 2008',
  tags: ['Limited Liability', 'Flexible Management', 'No Mandatory Audit (small LLPs)', 'Min 2 Partners', 'Designated Partners'],
  heroBg: 'linear-gradient(135deg, #eef0f6, #dfe4ef)',
  heroAccent: '#a9b3c9',
  titleColor: '#2b3a59',
  subtitleColor: '#3a4a66',
  tagBg: '#edeef3',
  tagText: '#2b3a59',
  accentColor: '#3a4a66',
  // Stats will be dynamically loaded
  stats: [],
  filings: [
    { form: 'LLP-11', title: 'Annual Return',           deadline: '30 May yearly',  penalty: 'Fee slab',   status: 'filed' },
    { form: 'LLP-8',  title: 'Statement of Accounts',   deadline: '30 Oct yearly',  penalty: 'Fee slab',   status: 'pending' },
    { form: 'DIR-3 KYC', title: 'Designated Partner KYC', deadline: '30 Jun / 3 yrs', penalty: 'DPIN blocked', status: 'filed' },
    { form: 'Form 3', title: 'LLP Agreement',           deadline: '30d from incorp', penalty: 'Fee slab',  status: 'filed' },
  ],
  requirements: [
    { icon: '👥', title: 'Partners',              desc: 'Minimum 2 partners · No maximum limit · At least 2 designated partners (DPs) required' },
    { icon: '🛡️', title: 'Liability',            desc: "Partners' liability limited to their capital contribution — personal assets protected" },
    { icon: '📄', title: 'LLP Agreement',         desc: 'Mandatory LLP Agreement governs mutual rights and duties of partners — File Form 3' },
    { icon: '📊', title: 'Audit Requirement',     desc: 'Audit mandatory only if turnover > ₹40 lakh or contribution > ₹25 lakh' },
    { icon: '🏛️', title: 'Meetings',             desc: 'No statutory requirement for board meetings — governed by LLP Agreement' },
    { icon: '💼', title: 'Annual Compliance',     desc: 'LLP-11 (Annual Return) by 30 May · LLP-8 (Accounts) by 30 October each year' },
  ],
  complianceHealth: [
    { label: 'LLP-11 Filed',    percent: 88, color: '#4a6b3f' },
    { label: 'LLP-8 Filed',     percent: 72, color: '#8a6a1f' },
    { label: 'DIR-3 KYC Done',  percent: 95, color: '#4a6b3f' },
  ],
};

const CACHE_KEY = 'companyType:llp';

export default function LLPPage() {
  // Stale-while-revalidate: the stats strip loads async, so on revisit it reset
  // to empty and repopulated. Seed the last real config from cache.
  const [dynamicConfig, setDynamicConfig] = useState(() => getView(CACHE_KEY) ?? CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await companiesAPI.getLLPStats();
        if (response.success && response.data) {
          const stats = response.data;
          const dynamicStats = [
            { label: 'LLPs in Portfolio',   value: stats.totalCompanies.toString(), color: 'text-[#3a4a66]' },
            { label: 'Fully Compliant',     value: stats.fullyCompliant.toString(), color: 'text-[#4a6b3f]' },
            { label: 'LLP-11 Overdue',      value: stats.overdueFilings.toString(), color: 'text-[#9a564a]' },
            { label: 'Pending Actions',     value: stats.pendingActions.toString(), color: 'text-[#8a6a1f]' },
          ];
          
          const nextConfig = { ...CONFIG, stats: dynamicStats };
          setDynamicConfig(nextConfig);
          setView(CACHE_KEY, nextConfig);
        }
      } catch (error) {
        console.error('Failed to fetch LLP stats:', error);
        // Keep the cached real stats on a failed refresh; fall back to zeros
        // only on a true first load with nothing cached.
        const defaultStats = [
          { label: 'LLPs in Portfolio',   value: '0', color: 'text-[#3a4a66]' },
          { label: 'Fully Compliant',     value: '0', color: 'text-[#4a6b3f]' },
          { label: 'LLP-11 Overdue',      value: '0', color: 'text-[#9a564a]' },
          { label: 'Pending Actions',     value: '0', color: 'text-[#8a6a1f]' },
        ];
        if (!hasView(CACHE_KEY)) {
          setDynamicConfig(prev => ({
            ...prev,
            stats: defaultStats
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <CompanyTypePage config={dynamicConfig} />;
  }

  return <CompanyTypePage config={dynamicConfig} />;
}
